import type { RefObject } from "react";
import { formatTime } from "./formatTime";
import type { MeetingClock } from "./meetingTimeline";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// `offset` is a stored offset, in recording time; `leadIn` moves it onto the
// meeting clock the player shows and takes (see meetingTimeline). It used to
// subtract a transcript-to-video offset and render nothing when that went
// negative, which left a matched row with neither a timestamp nor a
// "Not in transcript" chip.
export const TimestampBadge = ({
  offset,
  leadIn = 0,
  videoRef,
  quote,
}: {
  offset: number;
  leadIn?: number;
  videoRef: RefObject<MeetingClock | null>;
  quote?: string;
}) => {
  const videoTime = Math.max(0, offset + leadIn);

  const button = (
    <button
      type="button"
      onClick={() => {
        if (videoRef.current) {
          videoRef.current.currentTime = videoTime;
          videoRef.current.play();
        }
      }}
      className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-mono bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
      title={quote ? undefined : `Jump to ${formatTime(videoTime)}`}
    >
      {formatTime(videoTime)}
    </button>
  );

  if (!quote) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p className="text-xs italic">"{quote}"</p>
      </TooltipContent>
    </Tooltip>
  );
};

// Shown for tasks the matcher couldn't anchor to the transcript — inferred
// follow-ups that were never spoken in the recording. No fake timestamp.
export const NotInTranscriptChip = () => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded bg-muted/60 px-1.5 py-0.5 text-xs text-muted-foreground cursor-help">
        Not in transcript
      </span>
    </TooltipTrigger>
    <TooltipContent className="max-w-xs">
      <p className="text-xs">
        Inferred follow-up — this task wasn't found in the recording.
      </p>
    </TooltipContent>
  </Tooltip>
);
